<script setup lang="ts">
import { Badge as FBadge } from "frappe-ui";
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { getColor, getLightnessScale } from "@/dokos-ui/composables/colors";
import BIcon from "@/dokos-ui/components/chips/BIcon.vue";

type BButtonProps = {
  as?: any;
  to?: any;
  href?: string;
  theme?: string;
  variant?: "solid" | "soft" | "ghost" | "outline" | "text";
  currentVariant?: "solid" | "soft" | "ghost" | "outline" | "text";
  leadingIcon?: string | object;
  trailingIcon?: string | object;
  disabled?: boolean;
  loading?: boolean;
  error?: any;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  align?: "start" | "center" | "end";
  target?: "_self" | "_blank"
};

const props = defineProps<BButtonProps>();

const externalUrl = computed(() => {
  if (typeof props.href === "string") {
    return props.href;
  } else if (typeof props.to !== "string") {
    return null;
  } else if (
    props.to.startsWith("http://") ||
    props.to.startsWith("https://")
  ) {
    return props.to;
  }
});

const SIZE_CLASSES = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
};

const PAD_CLASSES = {
  xs: "px-1.5 py-0.5 rounded-sm",
  sm: "px-2 py-1 rounded-[6px]",
  md: "px-2 py-1.5 rounded",
  lg: "px-3 py-2 rounded",
  xl: "px-4 py-2.5 rounded",
  "2xl": "px-8 py-5 rounded",
};

const GAP_CLASSES = {
  xs: "gap-1",
  sm: "gap-1",
  lg: "gap-2",
  xl: "gap-3",
  "2xl": "gap-5",
};

const ICON_SIZES = {
  xs: "size-3",
  sm: "size-3",
  lg: "size-5",
  xl: "size-8",
  "2xl": "size-12",
};

const classes = computed(() => {
  const fontClass = SIZE_CLASSES[props.size ?? "md"] ?? SIZE_CLASSES["md"];
  let padClasses = PAD_CLASSES[props.size ?? "md"] ?? PAD_CLASSES["md"];
  // Remove padding on text variant
  if (props.variant == "text") {
    padClasses = "";
  }
  return [
    `BButton--${props.variant ?? "solid"}`,
    fontClass,
    padClasses,
    (props.loading || props.disabled) && "pointer-events-none opacity-75",
    props.variant == "solid" && "shadow",
  ];
});

const styles = computed(() => {
  let color = props.theme ?? "gray";
  if (props.error) {
    color = "red";
  }
  const scale = getLightnessScale(props.variant ?? "solid", color);
  if (!scale) {
    return;
  }

  const out = {
    "--fg-base": getColor(color, scale.text.base),
    "--fg-hover": getColor(color, scale.text.hover),
    "--fg-active": getColor(color, scale.text.active),
    "--bg-base": getColor(color, scale.bg.base),
    "--bg-hover": getColor(color, scale.bg.hover),
    "--bg-active": getColor(color, scale.bg.active),
  };

  if (props.currentVariant) {
    const scale = getLightnessScale(props.currentVariant, color);
    Object.assign(out, {
      "--fg-current": getColor(color, scale.text.base),
      "--bg-current": getColor(color, scale.bg.base),
    })
  }

  return out;
});

const componentProps = computed(() => {
  const baseProps = { class: classes.value, style: styles.value } as any;

  baseProps.target = props.target;
  if (externalUrl.value) {
    baseProps.target = props.target || "_blank";
    baseProps.href = externalUrl.value;
  } else if (props.to) {
    baseProps.to = props.to;
  }

  if (as.value === "button") {
    baseProps.type = "button";
  }

  return baseProps;
});

const as = computed(() => {
  if (props.as) {
    return props.as;
  } else if (externalUrl.value) {
    return "a";
  } else if (props.to) {
    return RouterLink;
  } else {
    return "button";
  }
});

const gap = computed(() => {
  return GAP_CLASSES[props.size ?? "md"] ?? "gap-1";
});

const iconSize = computed(() => {
  return ICON_SIZES[props.size ?? "md"] ?? "size-4";
});

function getIconProps(iconPropValue) {
  const iconName = typeof iconPropValue == "string" ? iconPropValue : "";
  const iconProps = typeof iconPropValue != "string" ? iconPropValue : {};
  return {
    name: iconName,
    size: iconSize.value,
    ...iconProps,
  };
}
</script>

<template>
  <component
    :is="as"
    v-bind="componentProps"
    class="BButton"
    :disabled="disabled"
  >
    <slot name="body">
      <div
        class="flex items-center"
        :class="[
          gap,
          align === 'center' && 'justify-center',
          align === 'end' && 'justify-end',
        ]"
      >
        <BIcon
          v-if="(!trailingIcon) && loading"
          name="loader"
          class="motion-safe:animate-spin"
          :size="iconSize"
        />
        <BIcon
          v-else-if="leadingIcon && error"
          name="alert-triangle"
          :size="iconSize"
        />
        <BIcon v-else-if="leadingIcon && !error && !loading" v-bind="getIconProps(leadingIcon)" />
        <slot />
        <BIcon
          class="ml-auto"
          v-if="trailingIcon && !loading && !error"
          v-bind="getIconProps(trailingIcon)"
        />
        <BIcon
          v-else-if="trailingIcon && error"
          name="alert-triangle"
          :size="iconSize"
        />
        <BIcon
          v-if="trailingIcon && loading"
          name="loader"
          class="motion-safe:animate-spin"
          :size="iconSize"
        />
        <FBadge
          theme="red"
          variant="subtle"
          size="sm"
          v-if="error"
          :title="error"
        >
          Error
        </FBadge>
      </div>
    </slot>
  </component>
</template>

<style>
.BButton {
  color: var(--fg, #0ff);
  background-color: var(--bg, #f00);
  @apply font-medium inline-block transition text-left cursor-pointer focus:outline-none focus-visible:ring focus-visible:ring-blue-400;

  --fg: var(--fg-base);
  --bg: var(--bg-base);
}
.BButton:hover {
  --fg: var(--fg-hover);
  --bg: var(--bg-hover);
}
.BButton:active {
  --fg: var(--fg-active);
  --bg: var(--bg-active);
}
/* .BButton.router-link-exact-active {
  text-decoration: underline;
} */
.BButton.router-link-exact-active:not(:hover, :active) {
  --fg: var(--fg-current, var(--fg-base));
  --bg: var(--bg-current, var(--bg-base));
}

.BButton--outline {
  box-shadow: inset 0 0 0 2px currentColor;
}
.BButton--outline:hover {
  box-shadow: inset 0 0 0 3px currentColor;
}

.BButton {
  text-underline-offset: 0.1em;
}
.BButton:is(:hover, :focus-visible):has(h1, h2, h3, h4, h5, h6)
  :is(h1, h2, h3, h4, h5, h6) {
  text-decoration: underline;
}
.BButton--text {
  text-decoration: underline;
}

.BButtonWrapper:has(:focus-visible) .BButton {
  --fg: var(--fg-hover);
  --bg: var(--bg-hover);
  @apply outline-none ring ring-blue-400;
}
</style>
